import { api } from '../../../services/api';

export type CardInvoiceStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'EXTRACTED'
  | 'RECONCILING'
  | 'CLOSED'
  | 'FAILED';
export type CardInvoiceItemKind =
  | 'PURCHASE'
  | 'INSTALLMENT'
  | 'FEE'
  | 'IOF'
  | 'INTEREST'
  | 'CREDIT'
  | 'ADJUSTMENT';
export type CardInvoiceItemStatus = 'PENDING' | 'REJECTED' | 'APPROVED';

export interface CreditCardDTO {
  id: string;
  companyId: string;
  name: string;
  issuer: string;
  brand: string | null;
  lastFour: string | null;
  closingDay: number | null;
  dueDay: number | null;
  defaultBankAccountId: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  defaultBankAccount?: { id: string; name: string; bankName: string } | null;
  /** Conta CREDIT do provedor vinculada ao cartão (2D). */
  openFinanceAccountId?: string | null;
}

export interface CardInvoiceDTO {
  id: string;
  cardId: string;
  companyId: string;
  referenceMonth: number;
  referenceYear: number;
  closingDate: string | null;
  dueDate: string | null;
  totalAmount: number;
  fileName: string;
  status: CardInvoiceStatus;
  errorMessage: string | null;
  settlementTransactionId: string | null;
  reconciledDelta: number | null;
  closedAt: string | null;
  createdAt: string;
  /** Origem: upload (PDF/OFX) ou fatura fechada trazida pelo Open Finance. */
  sourceFormat?: 'UPLOAD' | 'OPEN_FINANCE';
  externalBillId?: string | null;
  /** Itens pelo ciclo ou com item de fora: a revisão confere com o total do banco. */
  estimated?: boolean;
  card?: { id: string; name: string; issuer: string; defaultBankAccountId?: string | null };
}

export interface CardInvoiceItemDTO {
  id: string;
  invoiceId: string;
  postDate: string;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  kind: CardInvoiceItemKind;
  installmentSeq: number | null;
  installmentTotal: number | null;
  suggestedCategoryId: string | null;
  categorySource: 'HISTORY' | 'NONE' | 'RULE' | 'MANUAL';
  /** Regra que sugeriu a categoria (só sugere; o item continua pendente). */
  classificationRuleId?: string | null;
  status: CardInvoiceItemStatus;
  /** Rateio: empresa de quem é a despesa, quando não é a empresa do cartão. */
  beneficiaryCompanyId: string | null;
  financialTransactionId: string | null;
  suggestedCategory?: {
    id: string;
    name: string;
    type: 'INCOME' | 'EXPENSE';
    color: string;
  } | null;
}

/** Espelho exato de cardInvoicesService.getInvoiceWithItems (backend, Task 8). */
export interface CardInvoiceDetail {
  invoice: CardInvoiceDTO;
  settlementTransaction: {
    id: string;
    description: string;
    amount: number;
    transactionDate: string;
    bankAccountId: string | null;
  } | null;
  items: CardInvoiceItemDTO[];
  approvedSumCents: number;
  deltaCents: number | null;
}

/** Espelho de suggestSettlement (backend, Task 10). */
export interface SettlementSuggestionDTO {
  transactions: Array<{
    id: string;
    description: string;
    amount: number;
    type: 'INCOME' | 'EXPENSE';
    transactionDate: string;
  }>;
  net: number;
  deltaToTarget: number;
  confirmable: boolean;
  projectedDeltaCents: number | null;
}

/** Janela em que o backend procurou o débito — exibida quando nada é encontrado. */
export interface SettlementSearchWindowDTO {
  anchor: string;
  start: string;
  end: string;
  windowDays: number;
  anchorSource: 'DUE_DATE' | 'DUE_DAY' | 'DUE_DAY_AFTER_CLOSING' | 'MONTH_END';
}

export interface ApproveInvoiceResult {
  approved: number;
  imported: Array<{ itemId: string; financialTransactionId: string }>;
  errors: Array<{ itemId: string; code: 'NOT_FOUND' | 'NOT_PENDING' | 'NO_CATEGORY' }>;
  deferred: Array<{
    itemId: string;
    from: { month: number; year: number };
    to: { month: number; year: number };
  }>;
}

export interface CardInvoicePendencyDTO {
  id: string;
  status: 'EXTRACTED' | 'RECONCILING';
  totalAmount: number;
  card: { id: string; name: string };
}

export const cardsService = {
  list: (companyId: string, params?: { active?: boolean }) =>
    api.get('/financial/cards', { params: { companyId, ...params } }).then((r) => r.data),
  create: (data: Record<string, any>) => api.post('/financial/cards', data).then((r) => r.data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/financial/cards/${id}`, data).then((r) => r.data),
  remove: (id: string) => api.delete(`/financial/cards/${id}`).then((r) => r.data),
};

export const cardInvoicesService = {
  listByCard: (cardId: string, params?: { status?: string; page?: number; limit?: number }) =>
    api.get(`/financial/cards/${cardId}/invoices`, { params }).then((r) => r.data),

  getOne: (invoiceId: string) =>
    api
      .get<{ success: boolean; data: CardInvoiceDetail }>(`/financial/invoices/${invoiceId}`)
      .then((r) => r.data),

  upload: (args: { cardId: string; referenceMonth: number; referenceYear: number; file: File }) => {
    const fd = new FormData();
    fd.append('referenceMonth', String(args.referenceMonth));
    fd.append('referenceYear', String(args.referenceYear));
    fd.append('file', args.file);
    return api
      .post(`/financial/cards/${args.cardId}/invoices`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  patchItem: (
    invoiceId: string,
    itemId: string,
    patch: Partial<{
      suggestedCategoryId: string | null;
      type: 'INCOME' | 'EXPENSE';
      kind: CardInvoiceItemKind;
      description: string;
      status: 'REJECTED' | 'PENDING';
      beneficiaryCompanyId: string | null;
    }>
  ) => api.patch(`/financial/invoices/${invoiceId}/items/${itemId}`, patch).then((r) => r.data),

  addItem: (
    invoiceId: string,
    item: {
      postDate: string;
      description: string;
      amount: number;
      kind?: CardInvoiceItemKind;
      type?: 'INCOME' | 'EXPENSE';
      suggestedCategoryId?: string | null;
    }
  ) => api.post(`/financial/invoices/${invoiceId}/items`, item).then((r) => r.data),

  approve: (invoiceId: string, body: { itemIds: string[] }) =>
    api.post(`/financial/invoices/${invoiceId}/approve`, body).then((r) => r.data),

  unapprove: (invoiceId: string, body: { itemIds: string[] }) =>
    api.post(`/financial/invoices/${invoiceId}/unapprove`, body).then((r) => r.data),

  rejectAll: (invoiceId: string) =>
    api.post(`/financial/invoices/${invoiceId}/reject-all`).then((r) => r.data),

  // body {} SEM a chave settlementTransactionId → modo sugestão (Task 12).
  suggestSettlement: (invoiceId: string) =>
    api
      .post<{
        success: boolean;
        data: {
          suggestions: SettlementSuggestionDTO[];
          searchWindow?: SettlementSearchWindowDTO;
        };
      }>(`/financial/invoices/${invoiceId}/reconcile`, {})
      .then((r) => r.data),

  confirmSettlement: (invoiceId: string, settlementTransactionId: string) =>
    api
      .post(`/financial/invoices/${invoiceId}/reconcile`, { settlementTransactionId })
      .then((r) => r.data),

  unlinkSettlement: (invoiceId: string) =>
    api
      .post(`/financial/invoices/${invoiceId}/reconcile`, { settlementTransactionId: null })
      .then((r) => r.data),

  close: (invoiceId: string) =>
    api.post(`/financial/invoices/${invoiceId}/close`).then((r) => r.data),

  remove: (invoiceId: string) => api.delete(`/financial/invoices/${invoiceId}`).then((r) => r.data),

  pendencies: (closingId: string) =>
    api
      .get<{
        success: boolean;
        data: CardInvoicePendencyDTO[];
      }>(`/financial/closings/${closingId}/card-invoice-pendencies`)
      .then((r) => r.data),
};
